/* Feb-2026 · Refer-a-friend card.
 *
 * Shows the user's referral code, invited/paid count, and a copy button.
 * Rendered inside the My-Billing panel below the plan grid. Hidden when
 * the admin has disabled `referral_program`.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';   // Feb-2026 · i18n Wave 5
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Copy, Users } from 'lucide-react';
import { toast } from 'sonner';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

async function fetchJson(path, opts = {}) {
  const r = await fetch(`${API}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...opts,
  });
  if (!r.ok) throw new Error(`Request failed (${r.status})`);
  return r.json();
}

export default function ReferralCard() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redeem, setRedeem]   = useState('');
  const [busy, setBusy]       = useState(false);

  useEffect(() => {
    fetchJson('/referrals/me')
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  // Enabled=false → the endpoint still returns the code (idempotent
  // mint) so we can preview it, but we hide the CTA until admin flips.
  if (!data || !data.enabled) return null;

  const shareUrl = `${window.location.origin}/?ref=${data.code}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t('referral.copied'));
    } catch {
      toast.error(t('referral.copyFailed'));
    }
  };

  const submitRedeem = async (e) => {
    e.preventDefault();
    if (!redeem.trim()) return;
    setBusy(true);
    try {
      await fetchJson('/referrals/redeem', {
        method: 'POST',
        body: JSON.stringify({ code: redeem.trim().toUpperCase() }),
      });
      toast.success(t('referral.redeemSuccess'));
      setRedeem('');
    } catch (e) {
      toast.error(e?.message || t('referral.redeemFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="referral-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-[13px]">
          <Users className="h-4 w-4 text-primary" /> {t('referral.title')}
        </CardTitle>
        <p className="text-[11.5px] text-muted-foreground">
          <span dangerouslySetInnerHTML={{ __html: t('referral.subtitle', { amount: (data.grant_inviter / 1000).toFixed(0) }) }} />
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <Input
            readOnly
            value={shareUrl}
            className="h-8 font-mono-stat text-[11.5px]"
            data-testid="referral-share-url"
          />
          <Button size="sm" onClick={copy} data-testid="referral-copy-btn">
            <Copy className="h-3.5 w-3.5" /> {t('referral.copy')}
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-2 rounded-md border border-border p-2 text-center text-[11px]">
          <div><div className="font-mono-stat text-[14px] text-primary" data-testid="referral-code">{data.code}</div><div className="text-[10px] text-muted-foreground">{t('referral.yourCode')}</div></div>
          <div><div className="font-mono-stat text-[14px] text-primary" data-testid="referral-invited">{data.invited_count}</div><div className="text-[10px] text-muted-foreground">{t('referral.invited')}</div></div>
          <div><div className="font-mono-stat text-[14px] text-emerald-500" data-testid="referral-paid">{data.paid_conversion_count}</div><div className="text-[10px] text-muted-foreground">{t('referral.paidConversions')}</div></div>
        </div>

        <form onSubmit={submitRedeem} className="flex items-center gap-2">
          <Input
            placeholder={t('referral.redeemPlaceholder')}
            value={redeem}
            onChange={(e) => setRedeem(e.target.value)}
            className="h-8 text-[12px] uppercase"
            data-testid="referral-redeem-input"
          />
          <Button size="sm" type="submit" disabled={busy} data-testid="referral-redeem-btn">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} {t('referral.redeem')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
